import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AuthState } from "context/AuthContext";
import { allowAccess } from "utils/userAccessFun";
import { custom, departments } from "data/authData";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { GlobalState } from "context/GlobalContext";
import { shootSkuImagesAssoc } from "../../CreateShoot/data";

const minShootSkuImagesRequired = 2;

const PublishModal = ({ modal, setModal, submitFunction, selectedRow, setSelectedRow }) => {
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [validationRows, setValidationRows] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const fetchShootSkus = async () => {
			if (!modal || !selectedRow?.shoot_id) return;
			setLoading(true);
			try {
				const res = await axios.get(`/api/studio/shoot_skus_by_shoot_id/${selectedRow.shoot_id}`);
				setValidationRows(res.data.result ?? []);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: parseErrorMessage(error),
					severity: "error",
				});
				setValidationRows([]);
			} finally {
				setLoading(false);
			}
		};

		fetchShootSkus();
	}, [modal, selectedRow?.shoot_id]);

	const validationSummary = useMemo(() => {
		const rows = validationRows.map((row) => {
			const imageCount = row?.[shootSkuImagesAssoc]?.length ?? 0;
			return {
				i_code: row?.i_code,
				imageCount,
				isValid: imageCount >= minShootSkuImagesRequired,
			};
		});

		return {
			rows,
			allValid: rows.length > 0 && rows.every((row) => row.isValid),
			failedCount: rows.filter((row) => !row.isValid).length,
		};
	}, [validationRows]);

	const handleCloseModal = () => {
		setModal(false);
		setSelectedRow();
		setValidationRows([]);
	};
	const handleFormSubmit = async () => {
		if (!validationSummary.allValid) return;
		if (typeof submitFunction === "function") submitFunction(selectedRow);
	};

	return (
		<Dialog fullWidth maxWidth="md" open={modal} onClose={handleCloseModal}>
			<DialogTitle>PUBLISH SHOOT</DialogTitle>
			<DialogContent dividers>
				<Typography variant="body2" color="text.secondary" mb={2}>
					Every SKU in this shoot must have at least {minShootSkuImagesRequired} uploaded images before publish.
				</Typography>

				{loading ? (
					<Box minHeight={180} display="flex" alignItems="center" justifyContent="center" columnGap={1}>
						<CircularProgress size={20} />
						<Typography>Checking SKU image coverage...</Typography>
					</Box>
				) : (
					<>
						<Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
							<Typography variant="subtitle1">Shoot ID: {selectedRow?.shoot_id}</Typography>
							<Typography
								variant="subtitle2"
								color={validationSummary.allValid ? green[700] : red[700]}
								fontWeight={700}>
								{validationSummary.allValid
									? "Ready to publish"
									: `${validationSummary.failedCount} SKU(s) below the minimum image count`}
							</Typography>
						</Box>

						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>SKU ICODE</TableCell>
										<TableCell align="center">Uploaded Images</TableCell>
										<TableCell align="center">Required</TableCell>
										<TableCell align="center">Status</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{validationSummary.rows.map((row) => (
										<TableRow key={row.i_code}>
											<TableCell>{row.i_code}</TableCell>
											<TableCell align="center">{row.imageCount}</TableCell>
											<TableCell align="center">{minShootSkuImagesRequired}</TableCell>
											<TableCell align="center">
												<Typography
													variant="body2"
													fontWeight={700}
													color={row.isValid ? green[700] : red[700]}>
													{row.isValid ? "Pass" : "Missing Images"}
												</Typography>
											</TableCell>
										</TableRow>
									))}
									{!validationSummary.rows.length && (
										<TableRow>
											<TableCell colSpan={4} align="center">
												No SKUs found for this shoot.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button variant="outlined" onClick={handleCloseModal}>
					Cancel
				</Button>
				<Button
					autoFocus
					variant="contained"
					color="success"
					onClick={handleFormSubmit}
					disabled={
						loading ||
						!validationSummary.allValid ||
						!allowAccess(custom.SHOOT_PUBLISHER.key, user, departments.STUDIO.key)
					}>
					Confirm Publish
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default PublishModal;
